(() => {
    const sectionSelector = '[data-section-type="collection-template"], [data-section-type="search-template"]';
    const filterPanelObservers = new WeakMap();
    let isAnimatingFilters = false;
    let animationTimer = 0;
    let lastOpenAccordionId = null;

    let activeAccordionAnimations = 0;
    let isAccordionAnimating = false;

    let syncHeightFrame = 0;
    const syncCollectionFiltersHeight = (section) => {
      if (!section || window.innerWidth < 768) return;
      if (isAccordionAnimating) return;

      const drawer = section.querySelector('[data-collection-filters]');
      const form = drawer && drawer.querySelector('[data-collection-filters-form]');
      if (!drawer || !form) return;

      if (!drawer.classList.contains('collection__filters--visible')) return;

      cancelAnimationFrame(syncHeightFrame);
      syncHeightFrame = requestAnimationFrame(() => {
        if (isAccordionAnimating) return;
        const measuredHeight = Math.min(window.innerHeight, Math.max(400, Math.ceil(form.scrollHeight)));
        const panelHeightValue = `${measuredHeight}px`;

        if (drawer.style.getPropertyValue('--cherry-filters-panel-height') !== panelHeightValue) {
          drawer.style.setProperty('--cherry-filters-panel-height', panelHeightValue);
        }
      });
    };

    const ACCORDION_ANIMATION_MS = 320;
    const DRAWER_ANIMATION_MS = 400;

    const onAccordionAnimationComplete = (section) => {
      activeAccordionAnimations--;
      if (activeAccordionAnimations <= 0) {
        activeAccordionAnimations = 0;
        isAccordionAnimating = false;
        if (section && window.innerWidth >= 768) {
          syncCollectionFiltersHeight(section);
        }
      }
    };

    const closeAccordionItem = (trigger, container, section) => {
      if (!container || !trigger) return;
      if (container._cherryTimer) {
        clearTimeout(container._cherryTimer);
        container._cherryTimer = null;
      }
      if (container._cherryEndHandler) {
        container.removeEventListener('transitionend', container._cherryEndHandler);
        container._cherryEndHandler = null;
      }

      trigger.disabled = false;
      trigger.removeAttribute('disabled');

      const currentHeight = container.getBoundingClientRect().height;
      container.style.height = `${currentHeight}px`;
      container.style.overflow = 'hidden';
      void container.offsetHeight;

      trigger.classList.remove('is-expanded');
      trigger.setAttribute('aria-expanded', 'false');
      container.classList.remove('is-expanded');
      container.setAttribute('aria-expanded', 'false');

      const closeSymbol = trigger.querySelector('.filter-toggle-symbol');
      if (closeSymbol) closeSymbol.textContent = '+';

      activeAccordionAnimations++;
      isAccordionAnimating = true;

      requestAnimationFrame(() => {
        container.style.height = '0px';
      });

      let ended = false;
      const onEnd = (e) => {
        if (e && e.target !== container) return;
        if (e && e.propertyName && e.propertyName !== 'height') return;
        if (ended) return;
        ended = true;

        if (container._cherryEndHandler) {
          container.removeEventListener('transitionend', container._cherryEndHandler);
          container._cherryEndHandler = null;
        }
        if (container._cherryTimer) {
          clearTimeout(container._cherryTimer);
          container._cherryTimer = null;
        }
        if (!trigger.classList.contains('is-expanded')) {
          container.style.height = '0px';
          container.style.overflow = 'hidden';
        }
        onAccordionAnimationComplete(section);
      };

      container._cherryEndHandler = onEnd;
      container.addEventListener('transitionend', onEnd);
      container._cherryTimer = setTimeout(onEnd, ACCORDION_ANIMATION_MS + 30);
    };

    const openAccordionItem = (trigger, container, section) => {
      if (!container || !trigger) return;
      if (container._cherryTimer) {
        clearTimeout(container._cherryTimer);
        container._cherryTimer = null;
      }
      if (container._cherryEndHandler) {
        container.removeEventListener('transitionend', container._cherryEndHandler);
        container._cherryEndHandler = null;
      }

      trigger.disabled = false;
      trigger.removeAttribute('disabled');

      trigger.classList.add('is-expanded');
      trigger.setAttribute('aria-expanded', 'true');
      container.classList.add('is-expanded');
      container.setAttribute('aria-expanded', 'true');
      container.style.overflow = 'hidden';

      const openSymbol = trigger.querySelector('.filter-toggle-symbol');
      if (openSymbol) openSymbol.textContent = '-';

      const content = container.querySelector('[data-collapsible-content]') || container.firstElementChild || container;
      const targetHeight = Math.max(content.scrollHeight, content.offsetHeight);

      const currentHeight = container.style.height && container.style.height !== 'auto'
        ? container.getBoundingClientRect().height
        : 0;

      container.style.height = `${currentHeight}px`;
      void container.offsetHeight;

      activeAccordionAnimations++;
      isAccordionAnimating = true;

      requestAnimationFrame(() => {
        container.style.height = `${targetHeight}px`;
      });

      let ended = false;
      const onEnd = (e) => {
        if (e && e.target !== container) return;
        if (e && e.propertyName && e.propertyName !== 'height') return;
        if (ended) return;
        ended = true;

        if (container._cherryEndHandler) {
          container.removeEventListener('transitionend', container._cherryEndHandler);
          container._cherryEndHandler = null;
        }
        if (container._cherryTimer) {
          clearTimeout(container._cherryTimer);
          container._cherryTimer = null;
        }
        if (trigger.classList.contains('is-expanded')) {
          container.style.height = 'auto';
          container.style.overflow = '';
        }
        onAccordionAnimationComplete(section);
      };

      container._cherryEndHandler = onEnd;
      container.addEventListener('transitionend', onEnd);
      container._cherryTimer = setTimeout(onEnd, ACCORDION_ANIMATION_MS + 30);
    };

    const toggleFilterAccordion = (trigger) => {
      const dropdownId = trigger.getAttribute('aria-controls');
      const dropdown = dropdownId ? document.getElementById(dropdownId) : null;
      if (!dropdown) return;

      const drawer = trigger.closest('[data-collection-filters]');
      const section = trigger.closest(sectionSelector);
      const form = drawer && drawer.querySelector('[data-collection-filters-form]');
      if (!drawer || !form) return;

      const isCurrentlyOpen = trigger.classList.contains('is-expanded') ||
        dropdown.classList.contains('is-expanded') ||
        trigger.getAttribute('aria-expanded') === 'true' ||
        dropdown.getAttribute('aria-expanded') === 'true';

      let closingHeight = 0;
      let openingHeight = 0;

      if (isCurrentlyOpen) {
        lastOpenAccordionId = null;
        window.__cherryLastOpenAccordionId = null;
        window.__cherryAllAccordionsClosed = true;
        closingHeight = dropdown.getBoundingClientRect().height;
        closeAccordionItem(trigger, dropdown, section);
      } else {
        lastOpenAccordionId = dropdownId;
        window.__cherryLastOpenAccordionId = dropdownId;
        window.__cherryAllAccordionsClosed = false;

        const allTriggers = drawer.querySelectorAll('[data-collapsible-trigger]:not([data-show-more-trigger])');
        allTriggers.forEach((otherTrigger) => {
          if (otherTrigger === trigger) return;
          const otherId = otherTrigger.getAttribute('aria-controls');
          const otherDropdown = otherId ? document.getElementById(otherId) : null;
          const otherExpanded = otherTrigger.classList.contains('is-expanded') ||
            (otherDropdown && otherDropdown.classList.contains('is-expanded')) ||
            otherTrigger.getAttribute('aria-expanded') === 'true' ||
            (otherDropdown && otherDropdown.getAttribute('aria-expanded') === 'true');

          if (otherExpanded && otherDropdown) {
            closingHeight += otherDropdown.getBoundingClientRect().height;
            closeAccordionItem(otherTrigger, otherDropdown, section);
          }
        });

        const content = dropdown.querySelector('[data-collapsible-content]') || dropdown.firstElementChild || dropdown;
        openingHeight = Math.max(content.scrollHeight, content.offsetHeight);

        openAccordionItem(trigger, dropdown, section);
      }

      if (section && window.innerWidth >= 768) {
        const currentFormHeight = form.scrollHeight;
        const anticipatedHeight = Math.min(
          window.innerHeight,
          Math.max(400, Math.ceil(currentFormHeight - closingHeight + openingHeight))
        );
        const panelHeightValue = `${anticipatedHeight}px`;

        isAccordionAnimating = true;
        drawer.style.setProperty('--cherry-filters-panel-height', panelHeightValue);
      }
    };

    const initCollectionFilterPanel = (root = document) => {
      root.querySelectorAll(sectionSelector).forEach((section) => {
        const form = section.querySelector('[data-collection-filters-form]');
        if (!form || filterPanelObservers.has(form)) return;

        const scrollable = form.closest('.collection__filters-list');
        if (scrollable) scrollable.setAttribute('data-scroll-lock-scrollable', '');

        let debounceTimer = null;
        const scheduleSync = () => {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => {
            syncCollectionFiltersHeight(section);
          }, 60);
        };

        let resizeObserver = null;
        if ('ResizeObserver' in window) {
          resizeObserver = new ResizeObserver(scheduleSync);
          resizeObserver.observe(form);
        }

        filterPanelObservers.set(form, {resizeObserver});
      });
    };

    const restoreOpenAccordionState = (root = document) => {
      root.querySelectorAll(sectionSelector).forEach((section) => {
        const form = section.querySelector('[data-collection-filters-form]');
        if (!form) return;

        const allTriggers = form.querySelectorAll('[data-collapsible-trigger]:not([data-show-more-trigger])');

        allTriggers.forEach((trig) => {
          trig.disabled = false;
          trig.removeAttribute('disabled');
        });

        const activeId = lastOpenAccordionId || window.__cherryLastOpenAccordionId;

        if (activeId) {
          const targetDropdown = document.getElementById(activeId);
          const targetTrigger = form.querySelector(`[aria-controls="${activeId}"]`);

          allTriggers.forEach((otherTrigger) => {
            const otherId = otherTrigger.getAttribute('aria-controls');
            const otherDropdown = otherId ? document.getElementById(otherId) : null;
            if (otherTrigger !== targetTrigger) {
              otherTrigger.classList.remove('is-expanded');
              otherTrigger.setAttribute('aria-expanded', 'false');
              const sym = otherTrigger.querySelector('.filter-toggle-symbol');
              if (sym) sym.textContent = '+';
              if (otherDropdown) {
                otherDropdown.classList.remove('is-expanded');
                otherDropdown.setAttribute('aria-expanded', 'false');
                otherDropdown.style.height = '0px';
                otherDropdown.style.overflow = 'hidden';
              }
            }
          });

          if (targetDropdown && targetTrigger) {
            targetTrigger.classList.add('is-expanded');
            targetTrigger.setAttribute('aria-expanded', 'true');
            const sym = targetTrigger.querySelector('.filter-toggle-symbol');
            if (sym) sym.textContent = '-';
            targetDropdown.classList.add('is-expanded');
            targetDropdown.setAttribute('aria-expanded', 'true');
            targetDropdown.style.height = 'auto';
            targetDropdown.style.overflow = '';
            syncCollectionFiltersHeight(section);
          }
        } else if (!window.__cherryAllAccordionsClosed) {
          let foundFirstOpen = false;
          allTriggers.forEach((trig) => {
            const id = trig.getAttribute('aria-controls');
            const drop = id ? document.getElementById(id) : null;
            const isOpen = trig.classList.contains('is-expanded') || (drop && drop.classList.contains('is-expanded'));

            if (isOpen && !foundFirstOpen) {
              foundFirstOpen = true;
              lastOpenAccordionId = id;
              window.__cherryLastOpenAccordionId = id;
              trig.classList.add('is-expanded');
              trig.setAttribute('aria-expanded', 'true');
              if (drop) {
                drop.classList.add('is-expanded');
                drop.setAttribute('aria-expanded', 'true');
                drop.style.height = 'auto';
                drop.style.overflow = '';
              }
            } else {
              trig.classList.remove('is-expanded');
              trig.setAttribute('aria-expanded', 'false');
              if (drop) {
                drop.classList.remove('is-expanded');
                drop.setAttribute('aria-expanded', 'false');
                drop.style.height = '0px';
                drop.style.overflow = 'hidden';
              }
            }
          });

          allTriggers.forEach((trig) => {
            const sym = trig.querySelector('.filter-toggle-symbol');
            if (sym) {
              sym.textContent = trig.classList.contains('is-expanded') ? '-' : '+';
            }
          });
        } else {
          // All accordions were closed by user
          allTriggers.forEach((trig) => {
            trig.classList.remove('is-expanded');
            trig.setAttribute('aria-expanded', 'false');
            const sym = trig.querySelector('.filter-toggle-symbol');
            if (sym) sym.textContent = '+';
            const id = trig.getAttribute('aria-controls');
            const drop = id ? document.getElementById(id) : null;
            if (drop) {
              drop.classList.remove('is-expanded');
              drop.setAttribute('aria-expanded', 'false');
              drop.style.height = '0px';
              drop.style.overflow = 'hidden';
            }
          });
        }
      });
    };

    const setCollectionDropdownState = (section, isOpen, restoreFocus = true) => {
      if (!section) return;

      const dropdown = section.querySelector('[data-collection-dropdown]');
      const trigger = section.querySelector('[data-collection-dropdown-toggle], [data-collection-selector-toggle]');
      const menu = section.querySelector('[data-collection-dropdown-menu]');

      const drawer = section.querySelector('[data-collection-selector]');
      const overlay = section.querySelector('.cherry-collection-selector__overlay');
      if (drawer) drawer.classList.toggle('is-open', isOpen);
      if (overlay) overlay.classList.toggle('is-open', isOpen);

      if (dropdown) dropdown.classList.toggle('is-open', isOpen);
      if (menu) menu.classList.toggle('is-open', isOpen);
      if (trigger) {
        trigger.classList.toggle('is-open', isOpen);
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      const stickyBar = section.querySelector('#CollectionStickyBar, [data-collection-sticky-bar]');
      if (stickyBar) stickyBar.classList.toggle('has-dropdown-open', isOpen);

      const anySelectorOpen = document.querySelector('[data-collection-selector].is-open');
      document.body.classList.toggle('cherry-collection-selector-open', Boolean(anySelectorOpen));

      const anyDropdownOpen = document.querySelector('[data-collection-dropdown].is-open');
      document.body.classList.toggle('cherry-collection-dropdown-open', Boolean(anyDropdownOpen));

      if (isOpen) {
        if (drawer) {
          window.setTimeout(() => {
            const closeButton = drawer.querySelector('[data-collection-selector-close]');
            if (closeButton && drawer.classList.contains('is-open')) closeButton.focus();
          }, 300);
        }
      } else if (restoreFocus && trigger) {
        trigger.focus();
      }
    };

    const setCollectionSelectorState = setCollectionDropdownState;

    let drawerClosingTimer = null;
    const setCollectionFiltersState = (section, isOpen, restoreFocus = true) => {
      if (!section) return;

      const drawer = section.querySelector('[data-collection-filters]');
      const overlay = section.querySelector('[data-cherry-filters-overlay]');
      const trigger = section.querySelector('[data-toggle-filters]');
      if (!drawer || !trigger) return;

      const wasOpen = drawer.classList.contains('collection__filters--visible');
      if (wasOpen === isOpen) return;

      clearTimeout(drawerClosingTimer);

      if (isOpen) {
        drawer.classList.add('collection__filters--visible');
        if (overlay) overlay.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        trigger.classList.add('is-active');
        document.body.classList.add('cherry-collection-filters-open');
        document.body.classList.add('open-filter');
        document.body.classList.remove('cherry-collection-filters-closing');

        const scrollable = drawer.querySelector('.collection__filters-list');
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: scrollable}));

        window.setTimeout(() => {
          const closeButton = drawer.querySelector('[data-close-filters]');
          if (closeButton && drawer.classList.contains('collection__filters--visible')) closeButton.focus();
        }, DRAWER_ANIMATION_MS);
      } else {
        drawer.classList.remove('collection__filters--visible');
        if (overlay) overlay.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        trigger.classList.remove('is-active');
        document.body.classList.add('cherry-collection-filters-closing');

        drawerClosingTimer = window.setTimeout(() => {
          if (!drawer.classList.contains('collection__filters--visible')) {
            document.body.classList.remove('cherry-collection-filters-open');
            document.body.classList.remove('open-filter');
            document.body.classList.remove('cherry-collection-filters-closing');
            document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
          }
        }, DRAWER_ANIMATION_MS + 20);

        if (restoreFocus) {
          trigger.focus();
        }
      }
    };

    document.addEventListener('click', (event) => {
      const filtersToggle = event.target.closest('[data-toggle-filters]');
      if (filtersToggle) {
        window.__cherryDrawerWasOpen = undefined;
        const section = filtersToggle.closest(sectionSelector);
        const drawer = section && section.querySelector('[data-collection-filters]');
        event.preventDefault();
        event.stopPropagation();
        setCollectionSelectorState(section, false, false);
        setCollectionFiltersState(section, !drawer || !drawer.classList.contains('collection__filters--visible'));
        return;
      }

      const filtersClose = event.target.closest('[data-close-filters], [data-cherry-filters-overlay]');
      if (filtersClose) {
        event.preventDefault();
        event.stopPropagation();
        setCollectionFiltersState(filtersClose.closest(sectionSelector), false);
        return;
      }

      const collectionDropdownToggle = event.target.closest('[data-collection-dropdown-toggle], [data-collection-selector-toggle]');
      if (collectionDropdownToggle) {
        const section = collectionDropdownToggle.closest(sectionSelector);
        const dropdown = section && section.querySelector('[data-collection-dropdown], [data-collection-selector]');
        const isCurrentlyOpen = dropdown && dropdown.classList.contains('is-open');
        event.preventDefault();
        setCollectionFiltersState(section, false, false);
        setCollectionDropdownState(section, !isCurrentlyOpen);
        return;
      }

      const collectionSelectorClose = event.target.closest('[data-collection-selector-close]');
      if (collectionSelectorClose) {
        event.preventDefault();
        setCollectionDropdownState(collectionSelectorClose.closest(sectionSelector), false);
        return;
      }

      // Close open collection dropdowns on outside click
      if (!event.target.closest('[data-collection-dropdown]')) {
        document.querySelectorAll(sectionSelector).forEach((sec) => {
          if (sec.querySelector('[data-collection-dropdown].is-open, [data-collection-selector].is-open')) {
            setCollectionDropdownState(sec, false, false);
          }
        });
      }

      const resetBtn = event.target.closest('[data-filter-reset-button], .filter-clear-all, .collection__filters__reset');
      if (resetBtn) {
        const isClearAll = resetBtn.classList.contains('filter-clear-all') ||
                           resetBtn.classList.contains('collection__filters__reset') ||
                           (!resetBtn.classList.contains('filter__active__remove') && !resetBtn.hasAttribute('data-filter-tag-reset-button'));

        if (isClearAll) {
          event.preventDefault();

          const section = resetBtn.closest(sectionSelector) || document.querySelector(sectionSelector);
          const drawer = section && section.querySelector('[data-collection-filters]');
          const form = section && section.querySelector('[data-collection-filters-form]');
          const isDrawerOpen = drawer && drawer.classList.contains('collection__filters--visible');
          window.__cherryDrawerWasOpen = isDrawerOpen;

          // If the drawer was already open, ensure it stays open
          if (isDrawerOpen) {
            drawer.classList.add('collection__filters--visible');
            const overlay = section && section.querySelector('[data-cherry-filters-overlay]');
            if (overlay) overlay.classList.add('is-open');
            const trigger = section && section.querySelector('[data-toggle-filters]');
            if (trigger) {
              trigger.setAttribute('aria-expanded', 'true');
              trigger.classList.add('is-active');
            }
            document.body.classList.add('cherry-collection-filters-open');
            document.body.classList.add('open-filter');
            document.body.classList.remove('cherry-collection-filters-closing');
          } else {
            // If the drawer was closed, ensure it stays closed
            if (drawer) drawer.classList.remove('collection__filters--visible');
            const overlay = section && section.querySelector('[data-cherry-filters-overlay]');
            if (overlay) overlay.classList.remove('is-open');
            const trigger = section && section.querySelector('[data-toggle-filters]');
            if (trigger) {
              trigger.setAttribute('aria-expanded', 'false');
              trigger.classList.remove('is-active');
            }
            document.body.classList.remove('cherry-collection-filters-open');
            document.body.classList.remove('open-filter');
            document.body.classList.remove('cherry-collection-filters-closing');
          }

          // Uncheck all filter checkboxes.
          if (form) {
            form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
              cb.checked = false;
            });
            form.querySelectorAll('[data-field-price-min], [data-field-price-max], input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((inp) => {
              inp.value = '';
            });
          }

          updateActiveFilterCounts(section);

          // Submit the form to trigger the theme's AJAX update without full page reload
          if (form) {
            form.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      }
    }, true);

    // Dedicated accordion click and keydown handlers with capture-phase stopPropagation
    document.addEventListener('click', (event) => {
      const accordionTrigger = event.target.closest(
        '[data-collection-filters] [data-collapsible-trigger]:not([data-show-more-trigger])'
      );
      if (!accordionTrigger) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (!event.isTrusted) return;

      accordionTrigger.disabled = false;
      accordionTrigger.removeAttribute('disabled');

      toggleFilterAccordion(accordionTrigger);
    }, true);

    document.addEventListener('keydown', (event) => {
      const accordionTrigger = event.target.closest(
        '[data-collection-filters] [data-collapsible-trigger]:not([data-show-more-trigger])'
      );
      if (!accordionTrigger) return;

      if (event.key === 'Enter' || event.key === ' ' || event.code === 'Space') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        accordionTrigger.disabled = false;
        accordionTrigger.removeAttribute('disabled');

        toggleFilterAccordion(accordionTrigger);
      }
    }, true);

    window.addEventListener('resize', () => {
      document.querySelectorAll(sectionSelector).forEach(syncCollectionFiltersHeight);
    });

    document.addEventListener('click', (event) => {
      const densityButton = event.target.closest('[data-collection-density]');
      if (densityButton) {
        const section = densityButton.closest(sectionSelector);
        if (!section) return;

        const density = densityButton.dataset.collectionDensity;
        section.dataset.cherryGridDensity = density;
        section.querySelectorAll('[data-collection-density]').forEach((button) => {
          const isActive = button === densityButton;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        return;
      }
    });

    document.addEventListener('keydown', (event) => {
      const selectorDrawer = document.querySelector('[data-collection-selector].is-open');
      const filtersDrawer = document.querySelector('[data-collection-filters].collection__filters--visible');
      const drawer = selectorDrawer || filtersDrawer;
      if (!drawer) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        if (selectorDrawer) {
          setCollectionSelectorState(drawer.closest(sectionSelector), false);
        } else {
          setCollectionFiltersState(drawer.closest(sectionSelector), false);
        }
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const updateActiveFilterCounts = (section) => {
      if (!section) return;
      const form = section.querySelector('[data-collection-filters-form]');
      if (!form) return;

      const checkedInputs = Array.from(form.querySelectorAll('input[type="checkbox"]:checked'));
      const count = checkedInputs.length;

      section.querySelectorAll('[data-all-filter-count]').forEach((el) => {
        el.textContent = count > 0 ? ` (${count})` : '';
      });

      section.querySelectorAll('.filter-clear-all, [data-filter-reset-button]').forEach((el) => {
        el.classList.toggle('is-visible', count > 0);
      });

      const viewProducts = section.querySelector('[data-filter-view-products]');
      const closeFilter = section.querySelector('[data-filter-close-text]');
      if (viewProducts && closeFilter) {
        viewProducts.classList.remove('is-hidden');
        closeFilter.classList.add('is-hidden');
      }
    };

    document.addEventListener('change', (event) => {
      const target = event.target;
      const form = target && target.closest('[data-collection-filters-form]');
      if (form) {
        const section = form.closest(sectionSelector);
        if (section) {
          updateActiveFilterCounts(section);

          if (target.name === 'sort_by') {
            const label = target.nextElementSibling;
            const filterName = label && label.querySelector('.filter-name');
            const sortDisplay = form.querySelector('.current-sort-name');
            if (filterName && sortDisplay) {
              sortDisplay.textContent = filterName.textContent.trim();
            }
          }
        }
      }
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        document.querySelectorAll(sectionSelector).forEach((sec) => {
          if (sec.querySelector('[data-collection-dropdown].is-open, [data-collection-selector].is-open')) {
            setCollectionDropdownState(sec, false, true);
          }
        });
      }
    });

    document.addEventListener('scroll', (event) => {
      if (event.target && event.target.classList && event.target.classList.contains('collection__filters-list')) {
        window.__cherryFiltersScrollTop = event.target.scrollTop;
      }
    }, true);

    const initialize = () => requestAnimationFrame(() => {
      initCollectionFilterPanel();
      restoreOpenAccordionState();
      document.querySelectorAll(sectionSelector).forEach((sec) => {
        updateActiveFilterCounts(sec);
        if (window.__cherryDrawerWasOpen === false) {
          const drawer = sec.querySelector('[data-collection-filters]');
          if (drawer) drawer.classList.remove('collection__filters--visible');
          const overlay = sec.querySelector('[data-cherry-filters-overlay]');
          if (overlay) overlay.classList.remove('is-open');
          const trigger = sec.querySelector('[data-toggle-filters]');
          if (trigger) {
            trigger.setAttribute('aria-expanded', 'false');
            trigger.classList.remove('is-active');
          }
          document.body.classList.remove('cherry-collection-filters-open');
          document.body.classList.remove('open-filter');
          document.body.classList.remove('cherry-collection-filters-closing');
        }
      });
      if (typeof window.__cherryFiltersScrollTop === 'number' && window.__cherryFiltersScrollTop > 0) {
        const scrollList = document.querySelector('.collection__filters-list');
        if (scrollList) {
          scrollList.scrollTop = window.__cherryFiltersScrollTop;
          requestAnimationFrame(() => {
            scrollList.scrollTop = window.__cherryFiltersScrollTop;
          });
        }
      }
    });
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, {once: true});
    } else {
      initialize();
    }

    document.addEventListener('theme:filters:init', initialize);
    document.addEventListener('shopify:section:load', initialize);
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.querySelector('[data-collection-selector].is-open')) {
        document.body.classList.remove('cherry-collection-selector-open');
      }
      if (event.target.querySelector('[data-collection-filters].collection__filters--visible')) {
        document.body.classList.remove('cherry-collection-filters-open');
      }
    });
  })();
